// The hard-surface construction kit (overhaul Phase 2).
//
// Construction strategy adapted from TheLongSilence (github.com/achimala/TheLongSilence, MIT,
// (c) 2026 Anshu Chimala) -- specifically the place/weld/creaseNormals pattern and the
// absolute-metre chamfer floor. creaseNormals() below is a close adaptation of theirs; the
// attribution stays with it. The aesthetic is not taken; the construction law is.
//
// THE LAW:
//   1. Every generator returns a LIST of parts { geo, slot } in its own local space. A slot is a
//      material name ('hull', 'liner', 'trim', ...). Generators never make meshes or materials.
//   2. place(geo, {pos, rot, scale}) bakes the transform INTO the geometry and de-indexes it --
//      mergeGeometries refuses mixed index states, and de-indexed geometry is what the crease
//      pass needs anyway.
//   3. weldSlots(parts) groups by slot and merges each group into ONE BufferGeometry, then runs
//      creaseNormals(32 deg). The draw count of a finished assembly is the number of SLOTS, not
//      the number of parts.
//   4. Normals are NOT computeVertexNormals() -- that flattens every barrel into a prism and
//      mushes every chamfer. The crease pass averages face normals only where neighbouring faces
//      meet under the crease angle, area-weighted, with positions welded on a 4 mm lattice so
//      shading is continuous across part boundaries. Chamfers stay knife-sharp because a chamfer
//      meets its neighbours at 45/90 degrees; cylinders come out round because their facets meet
//      at a few degrees.
//
// THE ZERO-PRIMITIVE RULE: nothing here emits an untreated BoxGeometry face-on. beveledBox is the
// only box, and it always carries a chamfer with an ABSOLUTE floor -- a 4 m car panel and a 4 cm
// bracket get proportionate treatment, not the same fraction.

import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';

/** Chamfers below this stop reading at judging distance; below the floor they may as well not
 *  exist. In ABSOLUTE metres (the reference's hard-won lesson, kept). Callers can go below it
 *  explicitly for genuinely tiny parts via opts.bevel. */
export const CHAMFER_FLOOR = 0.012;

// ---------------------------------------------------------------------------
// transforms + welding
// ---------------------------------------------------------------------------

/** Bake a transform into a geometry so its vertices live in assembly space. De-indexes. */
export function place(geo, { pos = [0, 0, 0], rot = [0, 0, 0], scale = null } = {}) {
  const g = geo.index ? geo.toNonIndexed() : geo;
  if (scale) g.scale(scale[0], scale[1], scale[2]);
  if (rot[2]) g.rotateZ(rot[2]);
  if (rot[0]) g.rotateX(rot[0]);
  if (rot[1]) g.rotateY(rot[1]);
  g.translate(pos[0], pos[1], pos[2]);
  for (const name of Object.keys(g.attributes)) {
    if (name !== 'position' && name !== 'normal' && name !== 'uv') g.deleteAttribute(name);
  }
  g.morphAttributes = {};
  g.clearGroups();
  return g;
}

/** Apply place() to every part in a list, in-place, and return it. */
export function placeParts(parts, xform) {
  for (const p of parts) p.geo = place(p.geo, xform);
  return parts;
}

/**
 * Angle-threshold crease normals. Adapted from TheLongSilence gfx/greeble.js (MIT) -- see file
 * header. Average face normals across a shared position only where the faces meet under `deg`;
 * weight by the unnormalised cross product (= 2x triangle area, free); weld positions on a 4 mm
 * lattice so parts placed at exact coordinates shade continuously across their boundary.
 */
export function creaseNormals(geo, deg = 32) {
  const pos = geo.attributes.position;
  if (!pos || geo.index) return geo;
  const a = pos.array, nv = pos.count, nt = (nv / 3) | 0;
  const fn = new Float32Array(nt * 3);   // area-weighted face normal
  const fu = new Float32Array(nt * 3);   // unit face normal
  for (let t = 0; t < nt; t++) {
    const i = t * 9;
    const ux = a[i + 3] - a[i], uy = a[i + 4] - a[i + 1], uz = a[i + 5] - a[i + 2];
    const vx = a[i + 6] - a[i], vy = a[i + 7] - a[i + 1], vz = a[i + 8] - a[i + 2];
    const x = uy * vz - uz * vy, y = uz * vx - ux * vz, z = ux * vy - uy * vx;
    const l = Math.hypot(x, y, z) || 1;
    fn[t * 3] = x; fn[t * 3 + 1] = y; fn[t * 3 + 2] = z;
    fu[t * 3] = x / l; fu[t * 3 + 1] = y / l; fu[t * 3 + 2] = z / l;
  }
  const key = (x, y, z) => (
    ((Math.round(x * 256) + 32768) * 65536 + (Math.round(y * 256) + 32768)) * 65536
    + (Math.round(z * 256) + 32768));
  const at = new Map();
  for (let t = 0; t < nt; t++) {
    for (let k = 0; k < 3; k++) {
      const i = (t * 3 + k) * 3;
      const kk = key(a[i], a[i + 1], a[i + 2]);
      const b = at.get(kk);
      if (b === undefined) at.set(kk, [t]); else b.push(t);
    }
  }
  const lim = Math.cos(deg * Math.PI / 180);
  const out = new Float32Array(nv * 3);
  for (let t = 0; t < nt; t++) {
    const ux = fu[t * 3], uy = fu[t * 3 + 1], uz = fu[t * 3 + 2];
    for (let k = 0; k < 3; k++) {
      const i = (t * 3 + k) * 3;
      const b = at.get(key(a[i], a[i + 1], a[i + 2]));
      let x = 0, y = 0, z = 0;
      for (let m = 0; m < b.length; m++) {
        const o = b[m] * 3;
        if (fu[o] * ux + fu[o + 1] * uy + fu[o + 2] * uz < lim) continue;
        x += fn[o]; y += fn[o + 1]; z += fn[o + 2];
      }
      const l = Math.hypot(x, y, z);
      if (l > 1e-12) { out[i] = x / l; out[i + 1] = y / l; out[i + 2] = z / l; }
      else { out[i] = ux; out[i + 1] = uy; out[i + 2] = uz; }
    }
  }
  geo.setAttribute('normal', new THREE.BufferAttribute(out, 3));
  return geo;
}

/**
 * Group parts by slot, merge each group into one geometry, crease the normals.
 * Returns Map<slot, BufferGeometry>. Inputs are disposed.
 */
export function weldSlots(parts, creaseDeg = 32) {
  const bySlot = new Map();
  for (const p of parts) {
    if (!bySlot.has(p.slot)) bySlot.set(p.slot, []);
    bySlot.get(p.slot).push(p.geo);
  }
  const out = new Map();
  for (const [slot, list] of bySlot) {
    const g = mergeGeometries(list, false);
    if (!g) throw new Error(`weldSlots: merge failed for slot "${slot}" (${list.length} parts)`);
    creaseNormals(g, creaseDeg);
    g.computeBoundingSphere();
    out.set(slot, g);
    for (const x of list) x.dispose();
  }
  return out;
}

// ---------------------------------------------------------------------------
// generators
// ---------------------------------------------------------------------------

const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

/**
 * Chamfered box. The workhorse; the only box.
 *
 * opts:
 *   bevel      absolute metres (default max(CHAMFER_FLOOR, 4% of smallest side))
 *   cutCorners true  = 45-degree corner cuts in plan instead of rounded corners
 *   corners    [c0,c1,c2,c3] per-plan-corner radius multipliers (default all 1)
 *   panels     { faces:['pz','nz','px','nx'], nx, ny, gap, depth } -- splits the named faces into
 *              an nx x ny grid of raised plates over a recessed liner, the panel channels running
 *              continuously because plates and liner are welded into the same slots.
 *   slot       material slot for the shell (default 'hull'); liner always goes to 'liner'.
 */
export function beveledBox(w, h, d, opts = {}) {
  const slot = opts.slot || 'hull';
  const b = clamp(opts.bevel ?? Math.max(CHAMFER_FLOOR, Math.min(w, h, d) * 0.04),
    0.001, Math.min(w, h) * 0.45);
  const cs = opts.corners || [1, 1, 1, 1];
  const hw = w / 2, hh = h / 2;

  const shape = new THREE.Shape();
  const r = (i) => clamp(b * cs[i], 0.0005, Math.min(hw, hh) * 0.9);
  // Plan outline in XY, extruded along Z through depth d. Corners: rounded or 45-cut.
  const corner = (x, y, i, a0) => {
    const rr = r(i);
    if (opts.cutCorners) shape.lineTo(x - Math.cos(a0) * 0, y); // vertices supplied by caller path below
    return rr;
  };
  void corner;
  const r0 = r(0), r1 = r(1), r2 = r(2), r3 = r(3);
  shape.moveTo(-hw + r0, -hh);
  shape.lineTo(hw - r1, -hh);
  if (opts.cutCorners) shape.lineTo(hw, -hh + r1);
  else shape.quadraticCurveTo(hw, -hh, hw, -hh + r1);
  shape.lineTo(hw, hh - r2);
  if (opts.cutCorners) shape.lineTo(hw - r2, hh);
  else shape.quadraticCurveTo(hw, hh, hw - r2, hh);
  shape.lineTo(-hw + r3, hh);
  if (opts.cutCorners) shape.lineTo(-hw, hh - r3);
  else shape.quadraticCurveTo(-hw, hh, -hw, hh - r3);
  shape.lineTo(-hw, -hh + r0);
  if (opts.cutCorners) shape.lineTo(-hw + r0, -hh);
  else shape.quadraticCurveTo(-hw, -hh, -hw + r0, -hh);

  const bt = clamp(b, 0.001, d * 0.42);
  const g = new THREE.ExtrudeGeometry(shape, {
    depth: Math.max(0.002, d - bt * 2), bevelEnabled: true,
    bevelSize: b * 0.7, bevelThickness: bt, bevelSegments: 1, curveSegments: 2,
  });
  g.translate(0, 0, -(d - bt * 2) / 2);
  const parts = [{ geo: g, slot }];

  // Paneling: raised plates over a recessed liner. The channel is the GAP between plates -- no
  // boolean needed, and the liner showing through is what a panel line actually is.
  if (opts.panels) {
    const P = opts.panels;
    const gap = P.gap ?? 0.02, depth = P.depth ?? 0.015, nx = P.nx ?? 2, ny = P.ny ?? 2;
    for (const face of P.faces || ['pz']) {
      const horiz = face === 'px' || face === 'nx';
      const fw = horiz ? d : w, fh = h;
      const px = (horiz ? w / 2 : d / 2) - depth * 0.4;
      const cols = horiz ? Math.max(1, Math.round(nx * (d / w))) || nx : nx;
      const pw = (fw - gap * (cols + 1)) / cols, ph = (fh - gap * (ny + 1)) / ny;
      if (pw < 0.03 || ph < 0.03) continue;
      for (let ix = 0; ix < cols; ix++) {
        for (let iy = 0; iy < ny; iy++) {
          const cx = -fw / 2 + gap + pw * ix + gap * ix + pw / 2;
          const cy = -fh / 2 + gap + ph * iy + gap * iy + ph / 2;
          const plate = beveledBox(pw, ph, depth, { bevel: Math.min(0.012, depth * 0.4), slot })[0].geo;
          const rotY = face === 'px' ? Math.PI / 2 : face === 'nx' ? -Math.PI / 2 : face === 'nz' ? Math.PI : 0;
          place(plate, { rot: [0, rotY, 0] });
          const off = face === 'pz' ? [cx, cy, d / 2 + depth / 2 - 0.002]
            : face === 'nz' ? [-cx, cy, -d / 2 - depth / 2 + 0.002]
              : face === 'px' ? [px + depth / 2, cy, -cx]
                : [-px - depth / 2, cy, cx];
          place(plate, { pos: off });
          parts.push({ geo: plate, slot });
        }
      }
      // The liner behind the plates, slightly inset, darker slot.
      const liner = new THREE.ExtrudeGeometry(shape, {
        depth: Math.max(0.002, d - bt * 2), bevelEnabled: true,
        bevelSize: b * 0.55, bevelThickness: bt * 0.9, bevelSegments: 1, curveSegments: 2,
      });
      liner.translate(0, 0, -(d - bt * 2) / 2);
      liner.scale(0.998, 0.998, 0.998);
      parts.push({ geo: liner, slot: 'liner' });
      break; // one liner serves all faces
    }
  }
  return parts;
}

/**
 * Shape extrusion with real bevels; optionally along a path.
 *
 * opts: depth, bevel (absolute m), bevelSegments, curveSegments, path (THREE.Curve), steps, slot
 */
export function extrudeProfile(shape, opts = {}) {
  const slot = opts.slot || 'hull';
  const bevel = clamp(opts.bevel ?? CHAMFER_FLOOR, 0.001, 10);
  const cfg = {
    bevelEnabled: true,
    bevelSize: bevel, bevelThickness: opts.bevelThickness ?? bevel,
    bevelSegments: opts.bevelSegments ?? 1,
    curveSegments: opts.curveSegments ?? 4,
  };
  if (opts.path) { cfg.extrudePath = opts.path; cfg.steps = opts.steps ?? 24; }
  else cfg.depth = opts.depth ?? 0.1;
  const g = new THREE.ExtrudeGeometry(shape, cfg);
  if (!opts.path) g.translate(0, 0, -(cfg.depth) / 2);
  return [{ geo: g, slot }];
}

/**
 * Hydraulic piston, extension in [0,1]. Bore body with ring bands and a gland, polished rod with
 * an eye at the end. Returns { parts, boreParts, rodParts, attach } -- welded whole for static
 * builds, or the two halves separately so a creature can bolt bore to thigh and rod to shin and
 * let the RIG drive the extension (a welded archetype cannot animate; two bones can).
 */
export function hydraulicPiston(len, boreR, opts = {}) {
  const ext = clamp(opts.extension ?? 0.5, 0, 1);
  const boreLen = len * 0.56;
  const rodLen = len * 0.52;
  const travel = len - boreLen;          // how far the rod eye sits from the bore mouth at ext=1
  const rodR = boreR * 0.45;
  const seg = opts.seg ?? 10;

  const boreParts = [];
  const bp = (geo, slot) => boreParts.push({ geo, slot });
  // Body, mouth gland, ring bands, base eye. Built along +Y from the base eye at origin.
  bp(place(new THREE.CylinderGeometry(boreR, boreR, boreLen, seg), { pos: [0, boreLen / 2, 0] }), opts.slot || 'steelDark');
  bp(place(new THREE.CylinderGeometry(boreR * 1.18, boreR * 1.18, boreR * 0.7, seg), { pos: [0, boreLen - boreR * 0.3, 0] }), 'trim');
  for (const t of [0.16, 0.38, 0.60]) {
    bp(place(new THREE.CylinderGeometry(boreR * 1.09, boreR * 1.09, boreR * 0.28, seg), { pos: [0, boreLen * t, 0] }), 'trim');
  }
  bp(place(new THREE.CylinderGeometry(boreR * 0.62, boreR * 0.62, boreR * 1.5, seg), { pos: [0, 0, 0], rot: [0, 0, Math.PI / 2] }), 'trim');

  const rodParts = [];
  const rp = (geo, slot) => rodParts.push({ geo, slot });
  // Rod + eye, built along -Y from the rod eye at ITS origin (so each half is joint-local).
  rp(place(new THREE.CylinderGeometry(rodR, rodR, rodLen, seg), { pos: [0, -rodLen / 2, 0] }), 'chrome');
  rp(place(new THREE.CylinderGeometry(rodR * 1.5, rodR * 1.5, rodR * 2.4, seg), { pos: [0, 0, 0], rot: [0, 0, Math.PI / 2] }), 'trim');
  // Seal ring at the visible end of the rod (sits just outside the gland).
  rp(place(new THREE.CylinderGeometry(rodR * 1.22, rodR * 1.22, rodR * 0.5, seg), { pos: [0, -rodLen * 0.92, 0] }), 'rubber');

  // Static weld: rod eye positioned by extension.
  const eyeY = boreLen + travel * ext + rodR * 0.5;
  const parts = [
    ...boreParts.map((p) => ({ geo: p.geo.clone(), slot: p.slot })),
    ...placeParts(rodParts.map((p) => ({ geo: p.geo.clone(), slot: p.slot })), { pos: [0, eyeY, 0] }),
  ];
  return { parts, boreParts, rodParts, attach: { baseEye: [0, 0, 0], rodEye: [0, eyeY, 0], travel, boreLen } };
}

/** Socket where two limb segments meet: cap + seal ring + shoulder. Cylindrical or spherical. */
export function jointCap(r, opts = {}) {
  const seg = opts.seg ?? 12;
  const slot = opts.slot || 'trim';
  const parts = [];
  if (opts.spherical) {
    parts.push({ geo: place(new THREE.SphereGeometry(r, seg, Math.max(6, seg >> 1))), slot });
  } else {
    const w = opts.width ?? r * 1.3;
    parts.push({ geo: place(new THREE.CylinderGeometry(r, r, w, seg), { rot: [0, 0, Math.PI / 2] }), slot });
    parts.push({ geo: place(new THREE.CylinderGeometry(r * 1.12, r * 1.12, w * 0.34, seg), { rot: [0, 0, Math.PI / 2] }), slot: 'rubber' });
    // Shoulder plates either side.
    for (const s of [-1, 1]) {
      parts.push({ geo: place(new THREE.CylinderGeometry(r * 0.7, r * 0.85, w * 0.18, seg), { rot: [0, 0, Math.PI / 2], pos: [s * w * 0.58, 0, 0] }), slot });
    }
  }
  return parts;
}

/**
 * Multi-strand cable bundle along a spline. Strands are individually offset and desynchronised in
 * sag so the bundle reads as a bundle, not one thick tube. Anchor clamps at the endpoints.
 */
export function cableRun(points, opts = {}) {
  const strands = opts.strands ?? 3;
  const r = opts.r ?? 0.012;
  const sag = opts.sag ?? 0.06;
  const slot = opts.slot || 'cable';
  const parts = [];
  const base = points.map((p) => new THREE.Vector3(...p));
  for (let s = 0; s < strands; s++) {
    const ang = (s / strands) * Math.PI * 2 + 0.7;
    const ox = Math.cos(ang) * r * 2.1, oz = Math.sin(ang) * r * 2.1;
    const pts = base.map((p, i) => {
      const mid = i > 0 && i < base.length - 1 ? 1 : 0;
      // Desynchronised sag: each strand droops by a different amount at interior points.
      const droop = mid * sag * (0.75 + 0.5 * ((s * 2654435761 % 97) / 97));
      return new THREE.Vector3(p.x + ox, p.y - droop, p.z + oz);
    });
    const curve = new THREE.CatmullRomCurve3(pts);
    parts.push({ geo: place(new THREE.TubeGeometry(curve, opts.tubular ?? 20, r, 5, false)), slot });
  }
  if (opts.clamps !== false) {
    for (const end of [base[0], base[base.length - 1]]) {
      const clampParts = beveledBox(r * 6, r * 5, r * 4, { bevel: Math.min(0.008, r), slot: 'trim' });
      placeParts(clampParts, { pos: [end.x, end.y, end.z] });
      parts.push(...clampParts);
    }
  }
  return parts;
}

/**
 * Chamfered rectangular section lofted along +Y with per-station size/offset -- the cure for
 * "a uniform section reads as placed rather than designed" (pattern from the reference's
 * loftBox, re-derived for Y-axis limbs). Stations: [y, halfX, halfZ, xOff, zOff], increasing y.
 * Chamfer in absolute metres, constant along the span. Flat faces; creaseNormals rounds nothing
 * here because every joint exceeds the crease angle -- which is the point.
 */
export function loftBox(stations, cham = 0.02, opts = {}) {
  const slot = opts.slot || 'hull';
  const N = 8;
  const rings = stations.map(([y, hx, hz, xo = 0, zo = 0]) => {
    const cx = Math.min(cham, hx * 0.6), cz = Math.min(cham, hz * 0.6);
    return {
      y,
      pts: [
        [hx, -hz + cz], [hx, hz - cz], [hx - cx, hz], [-hx + cx, hz],
        [-hx, hz - cz], [-hx, -hz + cz], [-hx + cx, -hz], [hx - cx, -hz],
      ].map(([x, z]) => [x + xo, z + zo]),
    };
  });
  const pos = [];
  // Winding: the ring point order runs CLOCKWISE seen from +Y, so the naive (a,b,c)(a,c,d)
  // emission put every side-face normal INWARD -- the bench rendered the whole flank pure black
  // (lit only from behind) with the bottom cap glowing through. Reversed emission; caps swapped
  // to match. The bench caught it in the first isolated frame, which is exactly why the brief
  // demands the toolkit be judged alone before any entity wears it.
  const quad = (a, b, c, d) => { pos.push(...a, ...c, ...b, ...a, ...d, ...c); };
  for (let i = 0; i < rings.length - 1; i++) {
    const A = rings[i], B = rings[i + 1];
    for (let k = 0; k < N; k++) {
      const k2 = (k + 1) % N;
      quad(
        [A.pts[k][0], A.y, A.pts[k][1]], [A.pts[k2][0], A.y, A.pts[k2][1]],
        [B.pts[k2][0], B.y, B.pts[k2][1]], [B.pts[k][0], B.y, B.pts[k][1]]);
    }
  }
  const cap = (ring, up) => {
    const c = ring.pts.reduce((s, p) => [s[0] + p[0], s[1] + p[1]], [0, 0]).map((v) => v / N);
    for (let k = 0; k < N; k++) {
      const k2 = (k + 1) % N;
      const a = [ring.pts[k][0], ring.y, ring.pts[k][1]];
      const b = [ring.pts[k2][0], ring.y, ring.pts[k2][1]];
      const m = [c[0], ring.y, c[1]];
      if (up) pos.push(...b, ...a, ...m); else pos.push(...a, ...b, ...m);
    }
  };
  if (opts.capA !== false) cap(rings[0], false);
  if (opts.capB !== false) cap(rings[rings.length - 1], true);
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.BufferAttribute(new Float32Array(pos), 3));
  // uv zeros + provisional normals: city buckets merge per material, and mergeGeometries refuses
  // attribute sets that differ -- a loft without uv would poison whichever bucket it lands in.
  g.setAttribute('uv', new THREE.BufferAttribute(new Float32Array((pos.length / 3) * 2), 2));
  g.computeVertexNormals();
  return [{ geo: g, slot }];
}

/** Tapered strut between two points. Low radial count on purpose; the crease pass rounds it. */
export function strut(a, b, r0, r1 = r0, opts = {}) {
  const A = new THREE.Vector3(...a), B = new THREE.Vector3(...b);
  const len = A.distanceTo(B);
  const g = new THREE.CylinderGeometry(r1, r0, len, opts.seg ?? 8);
  const dir = B.clone().sub(A).normalize();
  const q = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), dir);
  const m = new THREE.Matrix4().makeRotationFromQuaternion(q);
  m.setPosition(A.clone().add(B).multiplyScalar(0.5));
  g.applyMatrix4(m);
  return [{ geo: place(g), slot: opts.slot || 'hull' }];
}

/**
 * Articulated hand (user fix brief): palm base plate, four two-segment fingers with knuckle caps,
 * an opposable thumb at a natural angle, micro-servo cap at the wrist. Origin at the WRIST pivot,
 * hand extends -Y, palm faces +Z. ~0.19 m long overall at s=1.
 */
/**
 * Mirror a geometry across X. A negative-determinant transform flips triangle winding, which
 * would shade the mesh inside-out after the weld recomputes normals -- so every triangle's
 * vertices 1/2 are swapped across ALL attributes to restore outward winding. Returns a
 * NON-INDEXED geometry (the weld de-indexes anyway).
 */
export function mirrorX(geo) {
  const g = geo.index ? geo.toNonIndexed() : geo;
  const pos = g.attributes.position;
  for (let i = 0; i < pos.count; i++) pos.setX(i, -pos.getX(i));
  if (g.attributes.normal) {
    const nrm = g.attributes.normal;
    for (let i = 0; i < nrm.count; i++) nrm.setX(i, -nrm.getX(i));
  }
  for (const attr of Object.values(g.attributes)) {
    const a = attr.array, sz = attr.itemSize;
    for (let v = 0; v + 2 < pos.count; v += 3) {
      for (let c = 0; c < sz; c++) {
        const i1 = (v + 1) * sz + c, i2 = (v + 2) * sz + c;
        const t = a[i1]; a[i1] = a[i2]; a[i2] = t;
      }
    }
    attr.needsUpdate = true;
  }
  return g;
}

export function proceduralHand(s = 1, slot = 'chrome') {
  const P = [];
  const seg = (len, r) => new THREE.CapsuleGeometry(r, len, 2, 6);
  // Wrist servo cap + palm plate.
  P.push({ geo: place(new THREE.CylinderGeometry(0.021 * s, 0.024 * s, 0.03 * s, 8)), slot: 'trim' });
  P.push(...placeParts(beveledBox(0.072 * s, 0.075 * s, 0.024 * s, { bevel: 0.005 * s, slot }),
    { pos: [0, -0.062 * s, 0.004 * s] }));
  // Four fingers: proximal + distal capsules with a knuckle cap between.
  for (let f = 0; f < 4; f++) {
    const x = (-0.027 + f * 0.018) * s;
    const curl = 0.22 + f * 0.03;
    P.push({ geo: place(seg(0.030 * s, 0.0075 * s), { rot: [curl * 0.5, 0, 0], pos: [x, -0.115 * s, 0.006 * s] }), slot });
    P.push({ geo: place(new THREE.CylinderGeometry(0.008 * s, 0.008 * s, 0.014 * s, 6),
      { rot: [0, 0, Math.PI / 2], pos: [x, -0.135 * s, 0.010 * s] }), slot: 'trim' });
    P.push({ geo: place(seg(0.024 * s, 0.0065 * s), { rot: [curl, 0, 0], pos: [x, -0.158 * s, 0.016 * s] }), slot });
  }
  // Opposable thumb, built as an explicit CMC -> MCP -> tip CHAIN.
  //
  // The previous version placed two capsules by CENTRE with independent Euler rotations and
  // simply hoped they met. They did not: measured, the proximal capsule ended at
  // (-0.060, -0.062, 0.019) while the distal one began at (-0.067, -0.086, 0.034) -- 2.8 cm of
  // daylight on a 19 cm hand, which is the "broken thumb" the player sees, and it also pointed
  // the proximal bone back UP toward the wrist (rot [0.35,0,0.85] tilts its axis to
  // (-0.71,+0.62,+0.34) -- away from the fingers entirely).
  //
  // A chain cannot dislocate: every bone is built FROM one joint TO the next, so the endpoints
  // are shared by construction rather than by arithmetic that can drift. A ball at each joint
  // means no angle, however extreme, can open a seam -- the same reason the limbs use jointCap.
  // The CMC now sits high on the palm edge (y -0.046, was an effective -0.062), which is where a
  // real thumb's carpometacarpal joint lives: near the WRIST, not level with the knuckles.
  // Anatomy note, learned by RENDERING it: a thumb angled inward along its whole length ends up
  // INSIDE the palm plate (palm x spans +/-0.036) and vanishes -- the first cut of this fix was
  // invisible on the bench while every contact fence still read 0.000 m, because the fences
  // measure the palm pad, not the thumb. A real thumb ABDUCTS: the metacarpal swings ~35 deg
  // laterally OUT of the palm silhouette, and only the distal phalanx angles ~32 deg back inward
  // to face the index. That is what makes it both correct and visible.
  // Mount per the refinement brief: root at x = -0.52 * palmWidth, y = -0.35 * palmHeight (the
  // radial edge, high toward the wrist, where a carpometacarpal joint actually sits), a THENAR
  // wedge filling the mound between palm and thumb, then a metacarpal swung 40 deg out of the
  // palm plane (+Z) and 25 deg toward the index. Out-of-plane is what OPPOSITION means -- and,
  // usefully, it is also what keeps the thumb outside the palm's silhouette. (Sign note: the
  // brief writes the inward swing as -X; the index finger is at +X of the thumb root, so inward
  // is +X here. The stated intent -- "toward the index finger" -- is what is built.)
  const CMC = [-0.0374 * s, -0.0263 * s, 0.008 * s];
  const MCP = [-0.0244 * s, -0.0541 * s, 0.0337 * s];   // 40 deg forward, 25 deg inward, 40 mm
  const TIP = [-0.0124 * s, -0.0787 * s, 0.0460 * s];   // distal curls down and in, opposing
  // Thenar base wedge: the angled mound that carries the thumb off the palm. Without it the
  // metacarpal appears to sprout from a flat face, which is the "floating thumb" read.
  P.push(...placeParts(beveledBox(0.030 * s, 0.044 * s, 0.030 * s, { bevel: 0.006 * s, slot }),
    { rot: [0.42, 0, -0.34], pos: [-0.030 * s, -0.041 * s, 0.014 * s] }));
  P.push(...strut(CMC, MCP, 0.0098 * s, 0.0088 * s, { slot, seg: 8 }));
  P.push(...strut(MCP, TIP, 0.0086 * s, 0.0062 * s, { slot, seg: 8 }));
  P.push({ geo: place(new THREE.SphereGeometry(0.0108 * s, 8, 6), { pos: CMC }), slot: 'trim' });
  P.push({ geo: place(new THREE.SphereGeometry(0.0090 * s, 8, 6), { pos: MCP }), slot });
  P.push({ geo: place(new THREE.SphereGeometry(0.0062 * s, 8, 6), { pos: TIP }), slot });
  return P;
}

/**
 * Synthetic human face (user face brief): ONE anatomical side-profile polyline -- forehead slope,
 * recessed eye socket, nose bridge and tip, lip contours, angled jaw -- band-clipped into THREE
 * metallic plates (forehead / cheek+nose / jaw+chin) with 8 mm reveal gaps, each extruded across
 * the head width with bevelSegments 5 so the contours read organic, not boxy. All three plates
 * inherit the same silhouette, so they cannot disagree about where the nose is. Slot 'face' is
 * polished metal and is given its OWN material by the wearer. Origin = head-group origin; face
 * looks +Z.
 */
export function faceProfile(s = 1) {
  const mk = (pts, width) => {
    const sh = new THREE.Shape();
    sh.moveTo(pts[0][0] * s, pts[0][1] * s);
    for (let i = 1; i < pts.length; i++) sh.lineTo(pts[i][0] * s, pts[i][1] * s);
    sh.closePath();
    // Bevel is an ORDER smaller than the smallest facial feature. The first cut used 0.02/0.010
    // -- larger than the 4-16 mm nose/socket/lip reliefs -- and the whole anatomy vanished into
    // swollen metal boxes: the exact "features shallower than the bevel self-intersect and vanish"
    // failure the reference documents on panel(). Segments stay at 5 so what remains is organic.
    const g = new THREE.ExtrudeGeometry(sh, {
      depth: width * s, bevelEnabled: true,
      bevelSegments: 5, bevelThickness: 0.004 * s, bevelSize: 0.003 * s, curveSegments: 4,
    });
    g.translate(0, 0, -(width * s) / 2);
    return { geo: place(g, { rot: [0, -Math.PI / 2, 0] }), slot: 'face' };
  };
  return [
    // Forehead plate: brow ridge up to the crown.
    mk([[0.000, 0.192], [0.048, 0.178], [0.066, 0.138], [0.075, 0.127], [0.040, 0.124],
        [0.000, 0.130], [-0.038, 0.150], [-0.046, 0.174], [-0.020, 0.188]], 0.118),
    // Cheek plate: socket recess and cheek line, FLATTENED at the nose region -- a side-profile
    // extrusion is a flat wall from the front, so the nose is carried by a separate narrow fin
    // standing proud of this plate; that is what gives the front view an actual nose line.
    mk([[0.048, 0.116], [0.058, 0.104], [0.062, 0.096], [0.066, 0.080], [0.062, 0.070],
        [0.068, 0.062], [0.030, 0.058], [0.000, 0.064], [-0.022, 0.078],
        [-0.026, 0.100], [-0.008, 0.112], [0.022, 0.114]], 0.106),
    // Nose fin: bridge, proud tip, base -- 22 mm wide, the front view's centreline anatomy.
    mk([[0.058, 0.108], [0.072, 0.100], [0.098, 0.078], [0.086, 0.066], [0.066, 0.064],
        [0.036, 0.068], [0.036, 0.102]], 0.022),
    // Jaw + chin plate: lip shelf, forward chin, hard jaw angle.
    mk([[0.062, 0.052], [0.070, 0.044], [0.068, 0.026], [0.052, 0.012], [0.002, 0.020],
        [-0.032, 0.036], [-0.022, 0.050], [0.014, 0.050]], 0.090),
  ];
}

/** FNV-1a checksum over a Float32Array's raw bits -- the determinism fence reads this. */
export function checksum(geoOrArray) {
  const arr = geoOrArray.attributes ? geoOrArray.attributes.position.array : geoOrArray;
  const u8 = new Uint8Array(arr.buffer, arr.byteOffset, arr.byteLength);
  let h = 0x811c9dc5;
  for (let i = 0; i < u8.length; i++) { h ^= u8[i]; h = Math.imul(h, 0x01000193); }
  return (h >>> 0).toString(16).padStart(8, '0');
}
